import { AUTH_TOKEN_LSK, AUTH_CHALLENGE_LSK, AUTH_REDIRECT_URI_LSK, AUTH_REFRESH_TOKEN_LSK, USER_ID_LSK } from 'app.constants'
import { createClient } from '@openauthjs/openauth/client'
import { env } from './env'
import { queryClient } from './query-client'

// an interface declaration to handle the window types:
declare global {
  interface Window {
    authRedirectURI: string | null
    authChallenge: string | null
    authToken: string | null
    authRefreshToken: string | null
    userId: string | null
  }
}

export const useAuthentication = () => {

  const isLoggedIn = getAuthToken() !== null

  const login = (accessToken: string) => {
    updateAccessToken(accessToken)
  }

  const logout = () => {
    queryClient.removeQueries()
    updateAccessToken(null)
    redirectToAuthService()
  }

  return { login, isLoggedIn, logout }

}

export const isLoggedIn = () => getAuthToken() !== null

let isHardLoggingOut = false
export const getIsHardLoggingOut = () => isHardLoggingOut

export const hardLogout = () => {
  isHardLoggingOut = true
  queryClient.removeQueries()
  updateAccessToken(null)
  updateRefreshToken(null)
  updateAuthChallenge(null)
  updateUserId(null)
  const form = document.createElement('form')
  form.method = 'POST'
  form.action = `${env().AUTH_SERVICE_URL}/logout?redirect_uri=${encodeURIComponent(`${window.location.origin}/login`)}`
  document.body.appendChild(form)
  form.submit()
}

const redirectToAuthService = async () => {
  const redirectURI = `https://${window.location.hostname}/login?to=${getLocationFullPath()}`
  const { challenge, url } = await getClient().authorize(redirectURI, 'code', { pkce: true })
  updateAuthChallenge(JSON.stringify(challenge))
  location.href = url
}

const getLocationFullPath = () => (
  window.location.pathname +
  window.location.search +
  window.location.hash
)

let clientInstance: ReturnType<typeof createClient> | null = null

export const getClient = () => {
  if (!clientInstance) {
    clientInstance = createClient({
      clientID: 'SESAMI_ADMIN',
      issuer: env().AUTH_SERVICE_URL
    })
  }
  return clientInstance
}

// auth redirect uri
export const getAuthRedirectURI = () => getStorage(AUTH_REDIRECT_URI_LSK, window.authRedirectURI)
export const updateAuthRedirectURI = (uri: string | null) => {
  if (!updateStorage(AUTH_REDIRECT_URI_LSK, uri)) {
    window.authRedirectURI = uri
  }
}

// auth challenge
export const getAuthChallenge = () => getStorage(AUTH_CHALLENGE_LSK, window.authChallenge)
export const updateAuthChallenge = (challenge: string | null) => {
  if (!updateStorage(AUTH_CHALLENGE_LSK, challenge)) {
    window.authChallenge = challenge
  }
}

// auth token
export const getAuthToken = () => getStorage(AUTH_TOKEN_LSK, window.authToken)
export const updateAccessToken = (token: string | null) => {
  if (!updateStorage(AUTH_TOKEN_LSK, token)) {
    window.authToken = token
  }
}

// refresh token
export const getRefreshToken = () => getStorage(AUTH_REFRESH_TOKEN_LSK, window.authRefreshToken)
export const updateRefreshToken = (token: string | null) => {
  if (!updateStorage(AUTH_REFRESH_TOKEN_LSK, token)) {
    window.authRefreshToken = token
  }
}

// user id
export const getUserId = () => getStorage(USER_ID_LSK, window.userId)
export const updateUserId = (userId: string | null) => {
  if (!updateStorage(USER_ID_LSK, userId)) {
    window.userId = userId
  }
}

const getStorage = (key: string, storedInWindow: string | null) => {
  let value = null
  try {
    value = localStorage.getItem(key)
  } catch (e) {
    value = storedInWindow
  }
  return value ?? null
}

export const updateStorage = (key: string, value: string | null) => {
  try {
    if (value) {
      localStorage.setItem(key, value)
    } else {
      localStorage.removeItem(key)
    }
    return true
  } catch (e) {
    return false
  }
}
